import React, { useEffect, useState } from 'react';
import { useLocation } from '@reach/router';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import Colors from '@components/shared/Colors';
import { TextVariant, Typography } from '@components/shared/design/Typography';
import { ARTICLE_PROGRESS_BAR_HEIGHT, BREAKPOINT, NAVBAR_HEIGHT } from '@constants';
import IconArrowDown from '@images/arrow-down-with-body.svg';
import IconArrowUp from '@images/arrow-up-with-body.svg';
import { HeaderItem } from '../utils/helpers';
import ArticleProgressBar from './ArticleProgressBar';

interface Props {
  titles: HeaderItem[];
  articleContentRef: React.MutableRefObject<HTMLDivElement | null>;
}

const SCROLL_OFFSET = -ARTICLE_PROGRESS_BAR_HEIGHT - NAVBAR_HEIGHT - 10;

export default function TableOfContents({ titles, articleContentRef }: Props) {
  const { t } = useTranslation();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  function handleOpen() {
    setOpen(!open);
  }

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    if (location.hash) {
      timeoutId = setTimeout(() => {
        const id = location.hash.substring(1);
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          window.scrollBy(0, SCROLL_OFFSET);
        }
      }, 100);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  const handleTitleClick = () => {
    setTimeout(() => {
      window.scrollBy(0, SCROLL_OFFSET);
    }, 200);
  };

  return (
    <Container>
      {titles.length > 0 ? (
        <HeaderContainer onClick={handleOpen}>
          <Typography $variant={TextVariant.BodyBoldLG}>{t('components.table-of-contents')}</Typography>
          {!open ? <IconArrowDown /> : <IconArrowUp />}
        </HeaderContainer>
      ) : null}
      <ContentContainer $hide={!open} onClick={handleOpen}>
        {titles.map((title, i) => (
          <AnchoredTitle href={`#${title.id}`} key={title.id} onClick={handleTitleClick}>
            <Typography $variant={TextVariant.BodyRegularMD}>{`${i + 1}. ${title.text}`}</Typography>
          </AnchoredTitle>
        ))}
      </ContentContainer>
      <ArticleProgressBar articleContentRef={articleContentRef} />
    </Container>
  );
}

const Container = styled.div`
  position: sticky;
  top: ${NAVBAR_HEIGHT - 1}px;
  z-index: 10;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  background: ${Colors.paleGold25};
`;

const HeaderContainer = styled.div`
  display: flex;
  padding: 16px 60px;
  justify-content: space-between;
  align-items: center;
  height: ${ARTICLE_PROGRESS_BAR_HEIGHT}px;

  padding: 16px 60px;
  @media only screen and (max-width: ${BREAKPOINT.tablet}px) {
    padding: 16px 32px;
  }
  @media only screen and (max-width: ${BREAKPOINT.mobileL}px) {
    padding: 16px;
  }
`;

interface ContentContainerProps {
  $hide: boolean;
}

const ContentContainer = styled.div<ContentContainerProps>`
  display: ${(props) => (props.$hide ? 'none' : 'flex')};
  flex-direction: column;
  align-items: flex-start;
  gap: 15px;
  overflow-y: scroll;
  max-height: calc(100vh - ${ARTICLE_PROGRESS_BAR_HEIGHT + NAVBAR_HEIGHT}px);

  padding: 0 60px 16px;
  @media only screen and (max-width: ${BREAKPOINT.tablet}px) {
    padding: 0 32px 16px;
  }
  @media only screen and (max-width: ${BREAKPOINT.mobileL}px) {
    padding: 0 16px 16px;
  }
`;

const AnchoredTitle = styled.a`
  text-align: start;

  p {
    line-height: 160%;
  }
`;
